import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { FamilyEvent } from '@chancenwerk/family/entities/FamilyEvent.js';
import { FamilyRegistration } from '@chancenwerk/family/entities/FamilyRegistration.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { of, switchMap } from 'rxjs';
import { AnmeldungPageRegistrationBase } from '../components/anmeldung-component-formular-base.js';

/**
 * The visual states that the page can enter.
 */
enum ACQUISITON_PAGE_STATES {
  /** Shows the registration form */
  'Register',
  /** Shows registration was successful */
  'Finished',
}

/**
 * Development-only switch to force a certain state to show up.
 */
const DEBUG_FORCE_STATE: ACQUISITON_PAGE_STATES | null = null;

/**
 * @customElement anmeldung-page-chancenfamily
 */
@customElement('anmeldung-page-chancenfamily')
export class AnmeldungPageChancenfamily extends AnmeldungPageRegistrationBase {
  @property({ attribute: false }) eventID = new RXController(this, null as Maybe<string>);

  @state() private readonly _size = new WindowSizeController(this);
  @state() private readonly _finished = new RXController(this, false);
  @state() private readonly _record = new RXController(
    this,
    this.eventID.observable.pipe(
      switchMap(id => {
        if (!id) {
          return of(new FamilyRegistration({}));
        }
        return of(
          new FamilyRegistration({
            eventID: id,
          }),
        );
      }),
    ),
  );
  @state() private readonly _event = new RXController(
    this,
    this.eventID.observable.pipe(
      switchMap(id =>
        subscribeByID(
          FamilyEvent,
          id ?? '',
        )({
          when: 1,
          publicName: 1,
        }),
      ),
    ),
  );

  @state() private readonly _form = new GenericFieldFormController(this, this._record.observable, {
    onSuccess: async doc => {
      await doc.create();
      this._finished.next(true);
    },
  });

  private _pageState() {
    if (A.isDevelopment && DEBUG_FORCE_STATE) {
      return DEBUG_FORCE_STATE;
    }

    if (this._finished.value) {
      return ACQUISITON_PAGE_STATES.Finished;
    }

    return ACQUISITON_PAGE_STATES.Register;
  }

  private async _submit() {
    await this._form.validate();
  }

  override render() {
    if (!this.eventID.value) {
      return nothing;
    }

    switch (this._pageState()) {
      case ACQUISITON_PAGE_STATES.Finished:
        return this._finishedState();
      case ACQUISITON_PAGE_STATES.Register:
        return this._regState();
    }

    throw new Error('unknown state');
  }

  private _regState() {
    return html`<cr-section
      ${css({
        height: '100%',
        overflowY: 'auto',
      })}
    >
      <cr-flex h100 w100 space=${this._size.mobile ? 'lg' : 'xl'}>
        <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
          <cr-flex class="headline" crossaxis-start end>
            <cr-flex class="h-content" space="lg" padding="lg">
              <cr-h1> CHANCENfamily Anmeldung </cr-h1>
            </cr-flex>
          </cr-flex>

          <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
            ${this._regStateContent()}
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-section>`;
  }

  private _regStateContent() {
    return html` <cr-flex crossaxis-center h100 w100 ${css({ paddingBottom: '300px' })}>
      <cr-flex crossaxis-center space="sm" ${css({ marginBottom: this.spacing.lg })}>
        <cr-text> Hier können Sie sich für ein Event der CHANCENfamily anmelden. </cr-text>
      </cr-flex>
      <cr-flex class="section-container" w100 space="lg" ${css({ maxWidth: '600px' })}>
        <cr-flex class="sc-content">
          <cr-flex space="lg" ${css({ minHeight: '300px' })}>
            <cr-flex space="md">
              <cr-text>Die Veranstaltung:</cr-text>
              <cr-text bold ${css({ fontSize: '1.2rem' })}>${this._event.value?.publicName}</cr-text>
              <cr-text ${css({ fontSize: '1.1rem', opacity: 0.8 })}>
                ${this._event.value?.when?.toFormat('dd.MM.yyyy HH:mm')}
              </cr-text>
              <cr-hr></cr-hr>
              <generated-form .formController=${this._form} .blacklist=${['eventID', 'status']}></generated-form>
              <cr-flex
                padding="md"
                space="md"
                ${css({
                  borderRadius: this.sizes.borderRadiusSecondary,
                  backgroundColor: this.colors.neutral.shade(50),
                })}
              >
                <cr-icon ${css({ fontSize: '1.2rem' })}>info</cr-icon>

                <cr-text>
                  Wenn Sie nicht so gut Deutsch sprechen, geben Sie bitte Ihre bevorzugte Sprache unter „hier Sprache
                  angeben“ an.<br /><br />

                  If you do not speak German very well, please indicate your preferred language under "hier Sprache
                  angeben."
                  <br /><br />

                  Almancayı çok iyi konuşamıyorsanız, lütfen tercih ettiğiniz dili "hier Sprache angeben" altında
                  belirtin.
                  <br /><br />

                  Если вы не очень хорошо говорите по-немецки, пожалуйста, укажите предпочтительный язык под «hier
                  Sprache angeben».
                  <br /><br />

                  Dacă nu vorbiți foarte bine germana, vă rugăm să indicați limba preferată sub „hier Sprache angeben”.
                  <br /><br />

                  ."hier Sprache angeben" إذا كنت لا تتحدث الألمانية جيدًا، فيرجى تحديد لغتك المفضلة تحت<br /><br />
                </cr-text>
              </cr-flex>
              <cr-button @click=${() => this._submit()}>Absenden</cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    </cr-flex>`;
  }

  private _finishedState() {
    return html`
      <cr-section
        ${css({
          height: '100%',
          overflowY: 'auto',
        })}
      >
        <cr-flex h100 w100 space=${this._size.mobile ? 'lg' : 'xl'}>
          <cr-flex crossaxis-center flex h100 space=${this._size.mobile ? 'lg' : 'xl'} ${css({ overflow: 'auto' })}>
            <cr-flex class="headline" crossaxis-start end>
              <cr-flex class="h-content" space="lg" padding="lg">
                <cr-h1> Fast geschafft… </cr-h1>
              </cr-flex>
            </cr-flex>

            <cr-flex class="form-content-page" space="xl" crossaxis-center h100 w100>
              <cr-icon ${css({ fontSize: '3.5rem', color: this.colors.primary })}>check</cr-icon>
              <cr-text bold>Vielen Dank für Ihre Anmeldung!</cr-text>
              <cr-text> Wir haben Ihre Anmeldung erfolgreich erhalten. </cr-text>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-section>
    `;
  }
}
